import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface NotesTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholderKey?: string;
  minRows?: number;
  maxRows?: number;
  id?: string;
  className?: string;
}

const LINE_HEIGHT = 20; // px, matches text-[13.5px]/leading-relaxed
const V_PADDING = 22; // top+bottom padding

// Auto-resizing textarea that grows with content between minRows and maxRows.
// Beyond maxRows it scrolls. Controlled (value/onChange).
export function NotesTextarea({
  value,
  onChange,
  placeholderKey = 'fields.addNotes',
  minRows = 3,
  maxRows = 8,
  id,
  className,
}: NotesTextareaProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const minH = minRows * LINE_HEIGHT + V_PADDING;
    const maxH = maxRows * LINE_HEIGHT + V_PADDING;
    // Reset to measure the natural scrollHeight, then clamp.
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, minH), maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
  }, [value, minRows, maxRows]);

  return (
    <textarea
      ref={ref}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t(placeholderKey)}
      rows={minRows}
      className={cn(
        'w-full resize-none rounded-md border border-line bg-paper px-3.5 py-[11px] text-[13.5px] leading-relaxed text-ink-2 outline-none placeholder:text-muted-2 focus-visible:border-line-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        className,
      )}
    />
  );
}
